/**
 * ⭐ THE PAGE HEADING — the deck's `.page-head` / `.page-title` / `.page-sub` / `.page-actions`,
 * established once.
 *
 * ## The defect, measured
 *
 * The owner said deck parity is *"super important"*, and the type hierarchy is the largest single
 * divergence left. Measured across `src/panes/*.tsx`:
 *
 * ```
 *   22 page titles at `text-base` (16px)   ← the deck's .page-title is --ds-text-2xl = 1.5rem (24px)
 *    1 page title  at `text-sm`   (14px)
 *    0 panes render a .page-sub at all
 * ```
 *
 * ⇒ **Every page title in the product is at least 1.5x too small**, and one is 1.7x. That is not a
 * cosmetic gap: section headings in these panes are `text-sm font-semibold`, so a 16px semibold page
 * title barely outranks them. **A three-tier hierarchy collapsed into one and a half** — which is
 * exactly the "where am I, and what is this screen" confusion the squint test is for. At 25% zoom the
 * current screens have no dominant element at all.
 *
 * The deck's values, used verbatim:
 *   · title    `--ds-text-2xl` = 1.5rem, weight 600, `letter-spacing: -0.015em`
 *     (⚠️ the negative tracking is not decoration — at 24px, default tracking looks loose and
 *     slightly amateur; it is what makes a large title read as typeset rather than merely big)
 *   · subtitle `--ds-text-sm` = 0.875rem, `color: secondary`, `margin-top: 4px`, `max-width: 78ch`
 *     (⚠️ 78ch, NOT the 100ch this package uses for body prose — a subtitle sits directly under a
 *     large title, and a measure close to the title's own width is what keeps the two reading as one
 *     block rather than as a heading with a paragraph stuck to it)
 *   · actions  `flex: none`, `gap: 8px`, aligned to the top of the title block
 *
 * ## ⛔ Two deliberate departures from the deck's CSS
 *
 * 1. **No `margin-bottom`.** `.page-head` has `margin-bottom: 20px`, but every consumer here is a
 *    child of a `flex flex-col gap-6` container (`PaneFrame`, or `PaneScrollLayout`'s `chrome` slot),
 *    which already owns the rhythm. A margin as well as a gap gives 44px in some places and 20px in
 *    others depending on what the parent is — the classic double-spacing bug. **Spacing belongs to
 *    the container, not to the item.**
 * 2. **`items-baseline` inside the title, not `items-center`.** 16 of the 22 existing titles put a
 *    badge or a count beside the text, and centring a small chip against 24px type floats it visibly
 *    above the baseline. Baseline alignment is what makes an adornment look attached to the word.
 *
 * ## ⛔ ONE `<h1>` PER SCREEN, and this component is what makes that structural
 *
 * The heading is an `<h1>` here and nowhere else in a pane. Two `<h1>`s on a screen give a screen
 * reader two competing answers to "what is this page", and the rail + breadcrumb already provide the
 * surrounding context. If a pane needs another prominent label, it is an `<h2>` — a *section*
 * heading — which is what `aria-labelledby` on its section already expects.
 */
import { useId, type ReactNode } from 'react';
import { Button } from './Button';
import { ButtonLink } from './ButtonLink';

/** The deck's `.page-title`. Exported so one definition is readable by a test. */
export const PAGE_TITLE_CLASS =
  'flex items-baseline gap-2 text-2xl font-semibold tracking-[-0.015em] text-primary';

/** The deck's `.page-sub` — including its own 78ch measure, narrower than body prose. */
export const PAGE_SUB_CLASS = 'mt-1 max-w-[78ch] text-sm text-secondary';

/**
 * ⭐⭐ **THE CREATE AFFORDANCE — a DISCRIMINATED UNION, so "exactly one destination" is a compile
 * error and not a comment.**
 *
 * ⛔ The shape this replaced had `to?` and `onSelect?` both optional with *"exactly one"* written
 * beside them. That constraint was enforced by **nothing**: neither and both were legal, and the two
 * bad states had to be caught by tests and documentation rather than by the compiler.
 *
 * ⚠️ **This is the same principle that created this prop in the first place.** Its sibling
 * `actions?: ReactNode` is documented as *"refresh, create, filters"* and typed as *anything at all*,
 * so **an untyped slot is an ungateable slot**: nothing can tell a refresh control from a second
 * primary action, an unlabelled icon, or a whole toolbar, and no test can be written that would fail
 * on the wrong one. A prose-only rule beside a permissive type is the identical failure one level
 * down — a rule nothing can check is a rule that will be broken.
 *
 * ⛔ **The rule above is stated WITHOUT an adoption count, deliberately.** This paragraph used to
 * argue it from *"passed by zero call sites for months"*, which stopped being true once the prop was
 * widely adopted — leaving a live rule justified by a number that had come to argue against it. The
 * defect an untyped slot permits does not get smaller as adoption grows; it gets more copies.
 *
 * ⛔ The `?: never` members are what make the arms mutually exclusive rather than merely different:
 * without them `{ label, to, onSelect }` satisfies the first arm and both destinations ship.
 *
 * ⚠️ **The third state — a label with no destination — is now UNREPRESENTABLE**, not merely tested.
 * A labelled control that goes nowhere advertises a capability and then denies it, and the type is a
 * better place to stop that than a `return null` no fixture could ever reach.
 */
export type PrimaryActionSpec =
  | { readonly label: string; readonly to: string; readonly onSelect?: never; readonly held?: PrimaryActionHeld }
  | { readonly label: string; readonly onSelect: () => void; readonly to?: never; readonly held?: PrimaryActionHeld };

/**
 * A primary action the page holds while it is busy, for example while a one-time token shows
 * (D5 of the G2 cleanup plan). `reason` is the sentence a screen reader hears on focus. The page
 * shows its own visible reason; the heading renders this one `sr-only`.
 */
export interface PrimaryActionHeld {
  readonly reason: string;
}

export interface PageHeadingProps {
  /**
   * The screen's name. A `ReactNode` so a badge or count can sit beside it on the baseline.
   *
   * ⚠️ Keep it a NAME, not a sentence — the deck's titles are one to three words. Anything longer
   * belongs in `subtitle`, where it gets a measure and the right size.
   */
  readonly title: ReactNode;
  /**
   * One or two sentences of orientation, at the deck's `.page-sub` treatment.
   *
   * ⚠️ Optional, and genuinely so — a screen whose name says everything should not be padded out with
   * a sentence restating it. But if the screen's *scope* is not obvious from its name (whose data?
   * which tenant? fleet-wide or scoped?), that belongs here rather than in a note further down.
   */
  readonly subtitle?: ReactNode;
  /**
   * Top-right SECONDARY controls — refresh, filters. `flex-none`, so the title wraps before they do.
   *
   * ⚠️ The create affordance is {@link PageHeadingProps.primaryAction}, not this. See its docblock:
   * an opaque `ReactNode` cannot be counted, and this slot spent months shipped, documented and
   * passed by nobody.
   */
  readonly actions?: ReactNode;
  /**
   * The page's PRIMARY create action, rendered rightmost and filled.
   *
   * ⛔ **A TYPED PROP, NOT `ReactNode`, AND THAT IS THE WHOLE POINT.** `actions?: ReactNode` already
   * existed — documented in this very file as *"refresh, create, filters"* — and was passed by **ZERO
   * call sites**. So *"no index screen has a create affordance"* (#1242) is a missing **call**, not a
   * missing component, and it stayed invisible to every gate for exactly as long as it was true:
   * **no scanner can ask "how many index screens offer a create action?" of an opaque child.** A named
   * prop makes "index screens with no `primaryAction`" a number.
   *
   * ⛔ **Permission-gated ⇒ pass `undefined` so it is ABSENT.** Never a disabled create: a door that
   * 403s is worse than no door. A missing permission is absent; a busy page is held: pass
   * `held: { reason }`, and the control keeps its place, reads `aria-disabled` and does nothing.
   *
   * ⛔ **`to` navigates, `onSelect` acts — EXACTLY ONE, and {@link PrimaryActionSpec} makes that a
   * COMPILE ERROR rather than a sentence in this docblock.** See its own note: a rule stated in prose
   * beside a type that does not encode it is a rule that will be broken, which is the same failure —
   * one level up — that left `actions` uncounted for months.
   */
  readonly primaryAction?: PrimaryActionSpec;
}

/**
 * ⛔ `tone="primary"` — FILLED. The one control on the page that should be found without looking for
 * it, and the measured defect behind the owner's *"nothing looks like a button"* was 89 of 91 controls
 * with no adequate boundary at rest.
 *
 * ## ⛔ NO `return null` ARM HERE, AND THAT IS DELIBERATE
 *
 * The obvious third arm — *"neither destination given ⇒ render nothing"* — is **unrepresentable**:
 * {@link PrimaryActionSpec} is a discriminated union, so a label with no destination does not type.
 * An unreachable arm is not free — it is a branch no fixture can enter, so it silently costs the file
 * its 100% and invites the next person to weaken the coverage config rather than delete dead code.
 * ⇒ the constraint lives in the type, which is the only place that can enforce it at zero cost.
 *
 * ⚠️ The `else` needs no `onSelect !== undefined` check: narrowing `to !== undefined` selects the
 * union's second arm, in which `onSelect` is required.
 *
 * A held action renders a `Button` for both arms, because an anchor cannot be held (`ButtonLink`).
 * It reads `aria-disabled`, keeps its Tab stop, names its reason by `aria-describedby` and has no
 * press handler, so a press does nothing.
 */
function PrimaryAction({ label, to, onSelect, held }: PrimaryActionSpec): React.JSX.Element {
  const reasonId = useId();
  if (held !== undefined) {
    return (
      <>
        <Button tone="primary" size="sm" aria-disabled="true" aria-describedby={reasonId} data-testid="page-primary-action">
          {label}
        </Button>
        <span id={reasonId} data-testid="page-primary-action-reason" className="sr-only">
          {held.reason}
        </span>
      </>
    );
  }
  if (to !== undefined) {
    return (
      <ButtonLink to={to} tone="primary" size="sm" data-testid="page-primary-action">
        {label}
      </ButtonLink>
    );
  }
  return (
    <Button tone="primary" size="sm" onClick={onSelect} data-testid="page-primary-action">
      {label}
    </Button>
  );
}

export function PageHeading({
  title,
  subtitle,
  actions,
  primaryAction,
}: PageHeadingProps): React.JSX.Element {
  return (
    <div className="flex items-start justify-between gap-6" data-testid="page-heading">
      {/* `min-w-0` so a long title wraps instead of pushing the actions off the right edge. */}
      <div className="min-w-0">
        <h1 className={PAGE_TITLE_CLASS} data-testid="page-title">
          {title}
        </h1>
        {subtitle === undefined ? null : (
          <p className={PAGE_SUB_CLASS} data-testid="page-sub">
            {subtitle}
          </p>
        )}
      </div>
      {/* ⛔ ABSENT, not empty — an always-rendered container still occupies its flex slot and still
          contributes its `gap-2`. The condition names BOTH slots: a screen with only a create action
          must still get the cluster, and a screen with neither must still get nothing. */}
      {actions === undefined && primaryAction === undefined ? null : (
        <div className="flex flex-none items-center gap-2" data-testid="page-actions">
          {actions}
          {/* ⛔ LAST, so the filled control is rightmost — the deck's `.page-actions` order. */}
          {primaryAction === undefined ? null : <PrimaryAction {...primaryAction} />}
        </div>
      )}
    </div>
  );
}
